import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
    Upload,
    RotateCw,
    FlipHorizontal2,
    FlipVertical2,
    RefreshCcw,
    ImagePlus,
    Eye,
    Layers,
    Info,
    ChevronDown,
    ZoomIn,
    Move,
} from "lucide-react";
import { toast } from "sonner";
import { PHONE_MODELS, getModelById } from "@/lib/phoneModels";
import { extractPalette } from "@/lib/colorExtractor";
import useSeo from "@/hooks/useSeo";
import CodeHunt from "@/components/CodeHunt";
import TikTokEmbed from "@/components/TikTokEmbed";

const VIEW_DESIGN = "design";
const VIEW_REALISTIC = "realistic";

// Templates are ~760x1520 — aspect ratio ~ 1:2. We preserve it in the stage.
const STAGE_ASPECT = 760 / 1520;

export default function PhoneCaseDesigner() {
    useSeo({
        title: "Phone Case Designer — Preview your case before you print",
        description:
            "Free online phone case designer. Upload an image, pick your phone model, adjust the fit, then print your case at a Vendagain vending machine in Perth.",
        canonicalPath: "/designer",
        keywords:
            "phone case designer, custom phone case Perth, phone case preview tool, design phone case online, phone case mockup",
    });
    const [selectedModelId, setSelectedModelId] = useState(PHONE_MODELS[0].id);
    const [imgSrc, setImgSrc] = useState(null);
    // Natural size of the uploaded image (for computing an initial "cover" fit)
    const [imgDims, setImgDims] = useState({ w: 0, h: 0 });
    const [x, setX] = useState(0);
    const [y, setY] = useState(0);
    const [scale, setScale] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [flipH, setFlipH] = useState(false);
    const [flipV, setFlipV] = useState(false);
    const [bgColor, setBgColor] = useState(null); // null = default light gray
    const [palette, setPalette] = useState([]);
    const [paletteLoading, setPaletteLoading] = useState(false);
    const [viewMode, setViewMode] = useState(VIEW_REALISTIC);
    const [modelMenuOpen, setModelMenuOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [stageSize, setStageSize] = useState({ w: 0, h: 0 });

    const stageWrapRef = useRef(null);
    const fileInputRef = useRef(null);
    const dragState = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });
    const pinchState = useRef({ active: false, startDist: 0, startScale: 1, startAngle: 0, startRot: 0 });

    const model = useMemo(() => getModelById(selectedModelId), [selectedModelId]);

    // Track stage size so we can compute correct image display dimensions
    useEffect(() => {
        const el = stageWrapRef.current;
        if (!el) return;
        const update = () => {
            const rect = el.getBoundingClientRect();
            setStageSize({ w: rect.width, h: rect.height });
        };
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const resetTransforms = useCallback(() => {
        setX(0);
        setY(0);
        setScale(1);
        setRotation(0);
        setFlipH(false);
        setFlipV(false);
        setBgColor(null);
        setPalette([]);
    }, []);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please choose an image file");
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            // Preload to get natural dimensions
            const img = new Image();
            img.onload = () => {
                setImgDims({ w: img.naturalWidth, h: img.naturalHeight });
                setImgSrc(ev.target.result);
                resetTransforms();
                toast.success("Image loaded — drag to reposition, scroll or pinch to zoom");
            };
            img.onerror = () => toast.error("Could not read that image, try another one");
            img.src = ev.target.result;
        };
        reader.readAsDataURL(file);
        // Allow re-picking the same file
        e.target.value = "";
    };

    const handleModelSelect = (id) => {
        setSelectedModelId(id);
        setModelMenuOpen(false);
        resetTransforms();
    };

    // Compute the image's base "cover" size — so it fills the case initially.
    const imgBase = useMemo(() => {
        if (!imgDims.w || !imgDims.h || !stageSize.w || !stageSize.h)
            return { w: stageSize.w || 0, h: stageSize.h || 0 };
        const stageR = stageSize.w / stageSize.h;
        const imgR = imgDims.w / imgDims.h;
        // "cover" — the image completely covers the stage
        if (imgR > stageR) {
            // image is wider — fit to stage height
            return { w: stageSize.h * imgR, h: stageSize.h };
        }
        return { w: stageSize.w, h: stageSize.w / imgR };
    }, [imgDims, stageSize]);

    // Auto-extract palette whenever a new image is loaded
    useEffect(() => {
        if (!imgSrc) {
            setPalette([]);
            return;
        }
        setPaletteLoading(true);
        extractPalette(imgSrc, 5).then((colors) => {
            setPalette(colors);
            setPaletteLoading(false);
        });
    }, [imgSrc]);

    // --- pointer / touch handling ---
    const onPointerDown = (e) => {
        if (!imgSrc) return;
        e.target.setPointerCapture?.(e.pointerId);
        dragState.current = {
            dragging: true,
            startX: e.clientX,
            startY: e.clientY,
            origX: x,
            origY: y,
        };
    };
    const onPointerMove = (e) => {
        if (!dragState.current.dragging) return;
        setX(dragState.current.origX + (e.clientX - dragState.current.startX));
        setY(dragState.current.origY + (e.clientY - dragState.current.startY));
    };
    const onPointerUp = () => {
        dragState.current.dragging = false;
    };

    const onWheel = (e) => {
        if (!imgSrc) return;
        e.preventDefault();
        setScale((s) => Math.max(0.1, Math.min(5, s + -e.deltaY * 0.0015)));
    };

    const onTouchStart = (e) => {
        if (e.touches.length === 2) {
            const [t1, t2] = [e.touches[0], e.touches[1]];
            const dx = t2.clientX - t1.clientX;
            const dy = t2.clientY - t1.clientY;
            pinchState.current = {
                active: true,
                startDist: Math.hypot(dx, dy),
                startScale: scale,
                startAngle: (Math.atan2(dy, dx) * 180) / Math.PI,
                startRot: rotation,
            };
        }
    };
    const onTouchMove = (e) => {
        if (e.touches.length === 2 && pinchState.current.active) {
            e.preventDefault();
            const [t1, t2] = [e.touches[0], e.touches[1]];
            const dx = t2.clientX - t1.clientX;
            const dy = t2.clientY - t1.clientY;
            const dist = Math.hypot(dx, dy);
            const ang = (Math.atan2(dy, dx) * 180) / Math.PI;
            const newScale =
                pinchState.current.startScale *
                (dist / pinchState.current.startDist);
            const newRot =
                pinchState.current.startRot + (ang - pinchState.current.startAngle);
            setScale(Math.max(0.1, Math.min(5, newScale)));
            setRotation(newRot);
        }
    };
    const onTouchEnd = (e) => {
        if (e.touches.length < 2) pinchState.current.active = false;
    };

    useEffect(() => {
        const el = stageWrapRef.current;
        if (!el || !imgSrc) return; // only intercept wheel when there's something to zoom
        const prevent = (e) => e.preventDefault();
        el.addEventListener("wheel", prevent, { passive: false });
        return () => el.removeEventListener("wheel", prevent);
    }, [imgSrc]);

    const imgTransform = `translate(-50%, -50%) translate(${x}px, ${y}px) rotate(${rotation}deg) scale(${scale * (flipH ? -1 : 1)}, ${scale * (flipV ? -1 : 1)})`;

    return (
        <div className="bg-white text-neutral-950">
            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
                <section className="mb-6 lg:mb-10 max-w-3xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-green-50 text-green-700 text-[11px] font-semibold uppercase tracking-wider mb-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                        Preview tool
                    </div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]" data-testid="hero-title">
                        See it before you print it.
                    </h1>
                    <p className="mt-3 text-neutral-600 text-base sm:text-lg leading-relaxed max-w-2xl">
                        Upload your image, pick your phone, and play with the fit.
                        A quick way to check your design will work — before you head
                        to the vending machine.
                    </p>
                </section>

                <section className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 lg:gap-8">
                    <div className="relative">
                        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                            <ModelPicker
                                model={model}
                                open={modelMenuOpen}
                                setOpen={setModelMenuOpen}
                                onSelect={handleModelSelect}
                            />
                            <div className="flex items-center gap-2">
                                <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
                                <button
                                    className="vg-pill vg-pill-outline text-xs h-[34px]"
                                    onClick={() => setHelpOpen(true)}
                                    data-testid="help-btn"
                                    aria-label="How it works"
                                >
                                    <Info size={14} />
                                </button>
                            </div>
                        </div>

                        <div
                            ref={stageWrapRef}
                            className="relative mx-auto bg-neutral-50 rounded-3xl border border-neutral-200 overflow-hidden vg-stage-shadow max-w-[290px] sm:max-w-[460px] lg:max-w-[400px]"
                            style={{
                                width: "100%",
                                // Slightly narrower on small screens so users have
                                // a clear left/right scroll gutter — prevents the
                                // drag handler capturing scroll gestures.
                                paddingLeft: 0,
                                paddingRight: 0,
                                aspectRatio: `${STAGE_ASPECT}`,
                            }}
                        >
                            {/* Mask container: clips whatever's inside to the case body + camera cutout shape */}
                            <div
                                className={`absolute inset-0 ${imgSrc ? "vg-no-select" : ""}`}
                                onPointerDown={onPointerDown}
                                onPointerMove={onPointerMove}
                                onPointerUp={onPointerUp}
                                onPointerCancel={onPointerUp}
                                onWheel={onWheel}
                                onTouchStart={onTouchStart}
                                onTouchMove={onTouchMove}
                                onTouchEnd={onTouchEnd}
                                style={{
                                    WebkitMaskImage: `url(${model.mask})`,
                                    maskImage: `url(${model.mask})`,
                                    WebkitMaskMode: "alpha",
                                    maskMode: "alpha",
                                    WebkitMaskSize: "100% 100%",
                                    maskSize: "100% 100%",
                                    WebkitMaskRepeat: "no-repeat",
                                    maskRepeat: "no-repeat",
                                    // Allow the page to scroll vertically when
                                    // no image is loaded (nothing to drag anyway),
                                    // otherwise capture all touch for pan/pinch.
                                    touchAction: imgSrc ? "none" : "auto",
                                }}
                                data-testid="editor-stage"
                            >
                                {/* Solid backdrop so the case body area is visible even without an image */}
                                <div
                                    className="absolute inset-0 transition-colors"
                                    style={{ backgroundColor: bgColor || "#f5f5f5" }}
                                />

                                {imgSrc && imgBase.w > 0 && (
                                    <img
                                        src={imgSrc}
                                        alt="Your design"
                                        draggable={false}
                                        className="absolute top-1/2 left-1/2 max-w-none pointer-events-none select-none"
                                        style={{
                                            width: `${imgBase.w}px`,
                                            height: `${imgBase.h}px`,
                                            transform: imgTransform,
                                            transformOrigin: "center center",
                                        }}
                                        data-testid="user-image"
                                    />
                                )}
                            </div>

                            {/* Empty state (clickable) — sits on TOP of mask container so users can tap */}
                            {!imgSrc && (
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-neutral-500 hover:text-neutral-900 transition-colors z-10"
                                    data-testid="empty-upload-btn"
                                >
                                    <div className="w-14 h-14 rounded-full bg-white border border-neutral-200 flex items-center justify-center shadow-sm">
                                        <ImagePlus size={22} />
                                    </div>
                                    <div className="text-sm font-medium text-neutral-800">
                                        Tap to upload an image
                                    </div>
                                    <div className="text-xs text-neutral-500 max-w-[220px] text-center">
                                        JPG, PNG or HEIC. Play with the fit —
                                        no download, no sign-up.
                                    </div>
                                </button>
                            )}

                            {/* Overlay: mockup for realistic, mask for design (as translucent hint) */}
                            <img
                                src={viewMode === VIEW_REALISTIC ? model.mockup : model.mask}
                                alt=""
                                draggable={false}
                                className="absolute inset-0 w-full h-full pointer-events-none select-none z-20"
                                style={{
                                    opacity: viewMode === VIEW_DESIGN && imgSrc ? 0.35 : 1,
                                }}
                                data-testid="overlay-image"
                            />

                            {viewMode === VIEW_DESIGN && imgSrc && (
                                <div className="absolute top-3 left-3 z-30 text-[10px] font-semibold uppercase tracking-wider text-red-600 bg-white/85 backdrop-blur px-2 py-1 rounded-md border border-red-200 pointer-events-none">
                                    Camera keep-out shown
                                </div>
                            )}
                        </div>

                        <p className="text-center text-xs text-neutral-500 mt-4 max-w-md mx-auto leading-relaxed">
                            Preview only. Head to the Vendagain vending machine to
                            create your real case — the final print may vary
                            slightly.
                        </p>
                    </div>

                    {/* Controls panel */}
                    <aside className="space-y-5">
                        <div className="bg-white border border-neutral-200 rounded-2xl p-5">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-sm font-semibold">Your image</h3>
                                {imgSrc && (
                                    <button
                                        onClick={() => {
                                            setImgSrc(null);
                                            setImgDims({ w: 0, h: 0 });
                                            resetTransforms();
                                        }}
                                        className="text-xs text-neutral-500 hover:text-neutral-900"
                                        data-testid="clear-image-btn"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleFileChange}
                                data-testid="file-input"
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                className="vg-pill vg-pill-primary w-full justify-center"
                                data-testid="upload-btn"
                            >
                                <Upload size={16} />
                                {imgSrc ? "Replace image" : "Upload image"}
                            </button>
                        </div>

                        <div
                            className={`bg-white border border-neutral-200 rounded-2xl p-5 space-y-5 transition-opacity ${imgSrc ? "opacity-100" : "opacity-50 pointer-events-none"}`}
                            data-testid="adjustments-panel"
                        >
                            <h3 className="text-sm font-semibold flex items-center gap-2">
                                <Move size={14} className="text-neutral-500" />
                                Adjust
                            </h3>

                            <ControlRow
                                label="Scale"
                                value={`${Math.round(scale * 100)}%`}
                                icon={<ZoomIn size={13} />}
                            >
                                <input
                                    type="range"
                                    min="10"
                                    max="400"
                                    step="1"
                                    value={Math.round(scale * 100)}
                                    onChange={(e) => setScale(Number(e.target.value) / 100)}
                                    className="vg-slider"
                                    data-testid="scale-slider"
                                />
                            </ControlRow>

                            <ControlRow
                                label="Rotation"
                                value={`${Math.round(rotation)}°`}
                                icon={<RotateCw size={13} />}
                            >
                                <input
                                    type="range"
                                    min="-180"
                                    max="180"
                                    step="1"
                                    value={Math.round(rotation)}
                                    onChange={(e) => setRotation(Number(e.target.value))}
                                    className="vg-slider"
                                    data-testid="rotation-slider"
                                />
                            </ControlRow>

                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    onClick={() => setFlipH((v) => !v)}
                                    className={`vg-pill ${flipH ? "vg-pill-accent" : "vg-pill-outline"}`}
                                    data-testid="flip-h-btn"
                                >
                                    <FlipHorizontal2 size={14} />
                                    Flip H
                                </button>
                                <button
                                    onClick={() => setFlipV((v) => !v)}
                                    className={`vg-pill ${flipV ? "vg-pill-accent" : "vg-pill-outline"}`}
                                    data-testid="flip-v-btn"
                                >
                                    <FlipVertical2 size={14} />
                                    Flip V
                                </button>
                            </div>

                            <button
                                onClick={resetTransforms}
                                className="vg-pill vg-pill-outline w-full justify-center"
                                data-testid="reset-btn"
                            >
                                <RefreshCcw size={14} />
                                Reset position
                            </button>
                        </div>

                        {/* Background control */}
                        <div
                            className={`bg-white border border-neutral-200 rounded-2xl p-5 space-y-4 transition-opacity ${imgSrc ? "opacity-100" : "opacity-50 pointer-events-none"}`}
                            data-testid="background-panel"
                        >
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-semibold flex items-center gap-2">
                                    <span
                                        className="w-4 h-4 rounded border border-neutral-300"
                                        style={{ backgroundColor: bgColor || "#f5f5f5" }}
                                    />
                                    Background
                                </h3>
                                {bgColor && (
                                    <button
                                        onClick={() => setBgColor(null)}
                                        className="text-xs text-neutral-500 hover:text-neutral-900"
                                        data-testid="bg-clear-btn"
                                    >
                                        Clear
                                    </button>
                                )}
                            </div>

                            {/* Palette extracted from image */}
                            <div>
                                <div className="text-xs font-medium text-neutral-600 mb-2">
                                    From your image
                                </div>
                                <div className="flex flex-wrap gap-2" data-testid="palette-swatches">
                                    {paletteLoading && palette.length === 0 && (
                                        <div className="text-xs text-neutral-400 py-1" data-testid="palette-loading">
                                            Sampling colours…
                                        </div>
                                    )}
                                    {!paletteLoading && palette.length === 0 && imgSrc && (
                                        <div className="text-xs text-neutral-400 py-1" data-testid="palette-empty">
                                            No dominant colours found.
                                        </div>
                                    )}
                                    {palette.map((c) => (
                                        <button
                                            key={c}
                                            onClick={() => setBgColor(c)}
                                            className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-110 ${bgColor === c ? "border-neutral-950 ring-2 ring-green-500 ring-offset-1" : "border-white shadow-sm ring-1 ring-neutral-200"}`}
                                            style={{ backgroundColor: c }}
                                            aria-label={`Set background to ${c}`}
                                            data-testid={`palette-${c}`}
                                        />
                                    ))}
                                </div>
                            </div>

                            {/* Custom color picker */}
                            <div>
                                <div className="text-xs font-medium text-neutral-600 mb-2">
                                    Or pick any colour
                                </div>
                                <div className="flex items-center gap-3">
                                    <label className="relative w-11 h-11 rounded-lg border border-neutral-200 overflow-hidden cursor-pointer hover:border-neutral-950 transition-colors">
                                        <input
                                            type="color"
                                            value={bgColor || "#ffffff"}
                                            onChange={(e) => setBgColor(e.target.value)}
                                            className="absolute inset-0 w-full h-full cursor-pointer"
                                            data-testid="bg-color-picker"
                                        />
                                    </label>
                                    <div className="font-mono text-xs text-neutral-500 flex-1">
                                        {bgColor || "No background"}
                                    </div>
                                </div>
                            </div>

                            {/* Common quick picks */}
                            <div>
                                <div className="text-xs font-medium text-neutral-600 mb-2">
                                    Common colours
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {["#000000", "#ffffff", "#f43f5e", "#f97316", "#22c55e", "#3b82f6", "#8b5cf6", "#ec4899"].map((c) => (
                                        <button
                                            key={c}
                                            onClick={() => setBgColor(c)}
                                            className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${bgColor === c ? "border-neutral-950 ring-2 ring-green-500 ring-offset-1" : "border-white shadow-sm ring-1 ring-neutral-200"}`}
                                            style={{ backgroundColor: c }}
                                            aria-label={`Set background to ${c}`}
                                            data-testid={`common-${c}`}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="bg-neutral-950 text-white rounded-2xl p-5">
                            <div className="text-[10px] uppercase tracking-widest text-green-400 font-semibold mb-2">
                                Tip
                            </div>
                            <p className="text-sm text-neutral-200 leading-relaxed">
                                Drag the image on the case to reposition. Scroll or
                                pinch to zoom. Two-finger twist on mobile to rotate.
                            </p>
                        </div>

                        {/* Magnetic upgrade teaser */}
                        <div className="relative rounded-2xl overflow-hidden border border-green-500/30 bg-gradient-to-br from-green-500 via-green-600 to-emerald-700 text-white p-5">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-8 h-8 rounded-md bg-white/20 backdrop-blur flex items-center justify-center">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M6 15l-4-4 6.75-6.77a7.79 7.79 0 0 1 11 11L13 22l-4-4 6.39-6.36a2.14 2.14 0 0 0-3-3z"/>
                                        <path d="M18 9l4 4"/>
                                        <path d="M15 4l4 4"/>
                                    </svg>
                                </div>
                                <div className="text-[10px] uppercase tracking-widest font-semibold">
                                    Premium tier
                                </div>
                            </div>
                            <div className="text-lg font-semibold leading-tight mb-1.5">
                                Upgrade to Magnetic
                            </div>
                            <p className="text-sm text-white/90 leading-relaxed">
                                Snap onto MagSafe stands, car holders and wireless
                                chargers. Available on selected models at the machine.
                            </p>
                        </div>

                        {/* Scavenger hunt code check */}
                        <CodeHunt />

                        {/* Featured TikTok video */}
                        <div className="rounded-2xl border border-neutral-200 bg-white p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <div className="w-8 h-8 rounded-md bg-neutral-950 text-white flex items-center justify-center">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                        <path d="M19.6 6.32a5.9 5.9 0 0 1-3.5-1.15A5.9 5.9 0 0 1 14.3 2h-3.4v13.36a2.7 2.7 0 1 1-2.7-2.7c.28 0 .56.05.82.13V9.36a6.1 6.1 0 0 0-.82-.06 6.09 6.09 0 1 0 6.1 6.09V9.06a9.36 9.36 0 0 0 5.3 1.65V7.28c-.01 0-.01-.05 0-.96Z" />
                                    </svg>
                                </div>
                                <div className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">
                                    Straight from the machine
                                </div>
                            </div>
                            <TikTokEmbed />
                        </div>
                    </aside>
                </section>
            </main>

            {helpOpen && <HelpModal onClose={() => setHelpOpen(false)} />}
        </div>
    );
}

function ModelPicker({ model, open, setOpen, onSelect }) {
    return (
        <div className="relative">
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-neutral-200 hover:border-neutral-900 rounded-full text-sm font-medium transition-colors"
                data-testid="model-picker-btn"
            >
                <span className="text-neutral-500 text-xs uppercase tracking-wider mr-1">
                    Model
                </span>
                <span>{model.name}</span>
                <ChevronDown
                    size={14}
                    className={`transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>
            {open && (
                <>
                    <button
                        className="fixed inset-0 z-40"
                        onClick={() => setOpen(false)}
                        aria-label="Close menu"
                    />
                    <div
                        className="absolute z-50 mt-2 min-w-[240px] bg-white border border-neutral-200 rounded-xl shadow-lg overflow-hidden"
                        data-testid="model-picker-menu"
                    >
                        {PHONE_MODELS.map((m) => (
                            <button
                                key={m.id}
                                onClick={() => onSelect(m.id)}
                                className={`w-full text-left px-4 py-3 text-sm hover:bg-neutral-50 flex items-center justify-between ${m.id === model.id ? "bg-neutral-50" : ""}`}
                                data-testid={`model-option-${m.id}`}
                            >
                                <span>
                                    <span className="text-neutral-400 text-xs mr-2">
                                        {m.brand}
                                    </span>
                                    {m.name}
                                </span>
                                {m.id === model.id && (
                                    <span className="w-2 h-2 rounded-full bg-green-500" />
                                )}
                            </button>
                        ))}
                        <div className="px-4 py-3 text-xs text-neutral-500 border-t border-neutral-100 bg-neutral-50">
                            More models coming soon
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

function ViewToggle({ viewMode, setViewMode }) {
    return (
        <div className="inline-flex bg-neutral-100 rounded-full p-1 text-xs font-medium" data-testid="view-toggle">
            <button
                onClick={() => setViewMode(VIEW_REALISTIC)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors ${
                    viewMode === VIEW_REALISTIC
                        ? "bg-white text-neutral-900 shadow-sm"
                        : "text-neutral-500 hover:text-neutral-900"
                }`}
                data-testid="view-realistic-btn"
            >
                <Eye size={13} />
                Realistic
            </button>
            <button
                onClick={() => setViewMode(VIEW_DESIGN)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors ${
                    viewMode === VIEW_DESIGN
                        ? "bg-white text-neutral-900 shadow-sm"
                        : "text-neutral-500 hover:text-neutral-900"
                }`}
                data-testid="view-design-btn"
            >
                <Layers size={13} />
                Design
            </button>
        </div>
    );
}

function ControlRow({ label, value, icon, children }) {
    return (
        <div>
            <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-neutral-600 flex items-center gap-1.5">
                    {icon}
                    {label}
                </span>
                <span className="font-mono text-xs text-neutral-900">{value}</span>
            </div>
            {children}
        </div>
    );
}

function HelpModal({ onClose }) {
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
            data-testid="help-modal"
        >
            <div
                className="bg-white rounded-2xl max-w-md w-full p-6 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">How it works</h2>
                    <button
                        onClick={onClose}
                        className="text-neutral-500 hover:text-neutral-900 text-2xl leading-none"
                        data-testid="help-close-btn"
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>
                <ol className="space-y-3 text-sm text-neutral-700">
                    <Step n={1}>Pick your phone model from the dropdown above the case.</Step>
                    <Step n={2}>Upload any image from your device.</Step>
                    <Step n={3}>Drag it into position. Use the sliders to scale and rotate, and the flip buttons to mirror it.</Step>
                    <Step n={4}>Switch between <b>Realistic</b> and <b>Design</b> views to see how it lays out on the case.</Step>
                    <Step n={5}>Happy with it? Head to the Vendagain vending machine and re-upload the same image to print your case.</Step>
                </ol>
                <div className="mt-5 pt-5 border-t border-neutral-100 text-xs text-neutral-500 leading-relaxed">
                    This tool is a preview only — a guide to help you plan your
                    design. The final print may look slightly different at the
                    machine.
                </div>
                <button
                    onClick={onClose}
                    className="vg-pill vg-pill-primary w-full justify-center mt-5"
                    data-testid="help-got-it-btn"
                >
                    Got it
                </button>
            </div>
        </div>
    );
}

function Step({ n, children }) {
    return (
        <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500 text-white text-xs font-semibold flex items-center justify-center">
                {n}
            </span>
            <span className="pt-0.5 leading-relaxed">{children}</span>
        </li>
    );
}
